import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import DailySales from "../../DailySales"
import { neumorph } from "../neumorph"
import { usePeriod } from "../PeriodContext"
import { formatCurrency } from "../KpiHeader"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

const DOW_ORDER = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const DayOfWeekCard = ({ supplier, retailer }) => {
  const { startDate, endDate } = usePeriod()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  const r = retailer?.toUpperCase()
  const isDaily = r === "PNP" || r === "CLI"
  const isWeekly = r === "CHK" || r === "MED" || r === "PET"
  const mode = isDaily ? "day" : isWeekly ? "week" : "none"

  useEffect(() => {
    if (!supplier || !retailer || mode === "none") {
      setData([])
      return
    }
    const endpoint = mode === "day" ? "day-of-week" : "week-of-month"
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/daily-sales/${endpoint}`,
          { supplier, retailer, userid: localStorage.getItem("userid"), startDate, endDate },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setData(res.data.data || [])
        else setData([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("DayOfWeekCard fetch error:", err)
      } finally {
        // a cancelled request (retailer / supplier switched) must not end the NEW request\'s loading state
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer, startDate, endDate, mode])

  if (mode === "none") {
    return (
      <BentoCard title="Day-of-Week Pattern" subtitle="Daily or weekly retailers only">
        <Flex h="100%" align="center" justify="center" direction="column" textAlign="center">
          <Text fontSize="xs" color={neumorph.textMuted}>
            Pattern analysis isn't available for {r} —
          </Text>
          <Text fontSize="2xs" color={neumorph.textMuted} mt={1}>
            data is monthly, not daily or weekly.
          </Text>
        </Flex>
      </BentoCard>
    )
  }

  // Normalize rows to { label, avg_amount, avg_units } + canonical sort
  const normalized = mode === "day"
    ? [...data].sort((a, b) => DOW_ORDER.indexOf(a.day_of_week) - DOW_ORDER.indexOf(b.day_of_week))
        .map((r) => ({ label: r.day_of_week, avg_amount: r.avg_amount, avg_units: r.avg_units }))
    : [...data].sort((a, b) => parseInt(a.week_of_month) - parseInt(b.week_of_month))
        .map((r) => ({ label: `Wk ${r.week_of_month}`, avg_amount: r.avg_amount, avg_units: r.avg_units }))

  const best = normalized.length
    ? normalized.reduce((m, r) => (parseFloat(r.avg_amount) > parseFloat(m.avg_amount) ? r : m))
    : null

  const chartData = normalized.length
    ? {
        labels: normalized.map((r) => r.label),
        datasets: [
          {
            label: "Avg Amount",
            data: normalized.map((r) => parseFloat(r.avg_amount) || 0),
            backgroundColor: normalized.map((r) =>
              best && r.label === best.label
                ? neumorph.accentGreen
                : "rgba(14, 124, 134, 0.6)"
            ),
            borderRadius: 4,
          },
        ],
      }
    : null

  const fmtCompactRand = (v) => {
    if (v >= 1_000_000) return `R${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `R${(v / 1_000).toFixed(0)}k`
    return `R${v}`
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: { display: true, grid: { display: false }, ticks: { font: { size: 9 }, color: neumorph.textMuted } },
      y: {
        display: true,
        beginAtZero: true,
        grid: { color: "rgba(174,184,200,0.15)", drawBorder: false },
        ticks: { font: { size: 9 }, color: neumorph.textMuted, maxTicksLimit: 4, callback: fmtCompactRand },
      },
    },
  }

  const cardTitle = mode === "day" ? "Best Selling Day" : "Best Selling Week"
  const cardSubtitle = mode === "day" ? "Average revenue by weekday" : "Average revenue by week of month"
  const topLabel = mode === "day" ? "Top day" : "Top week"
  const perLabel = mode === "day" ? "day" : "week"

  return (
    <>
      <BentoCard
        title={cardTitle}
        subtitle={cardSubtitle}
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        <Flex direction="column" h="100%" justify="space-between">
          {best && (
            <Box mb={2}>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                {topLabel}
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.accentGreen}>
                {best.label} · {formatCurrency(best.avg_amount)}/{perLabel}
              </Text>
            </Box>
          )}
          {chartData ? (
            <Box h="120px">
              <Bar data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Text fontSize="xs" color={neumorph.textMuted}>
              No data
            </Text>
          )}
        </Flex>
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Sales Volume & Patterns">
        <DailySales supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default DayOfWeekCard
